import { uid } from 'radash';
import { z } from 'zod';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ComponentPayloadValidator, ComponentValidator } from '../Component';
import { environmentalWithCoolingMethod } from '../component-sections/environmental';
import { ChargerConnector, PortPurpose } from '../fields';
import { AbstractMeasurement } from '../fields/AbstractMeasurement';
import { AbstractPort, Port } from '../fields/AbstractPort';
import { ChargerFeatures } from '../fields/ChargerFeatures';
import { IsolationVoltage } from '../fields/IsolationVoltage';
import { ParallelableCapacity } from '../fields/ParallelableCapacity';
import { Resistance } from '../fields/Resistance';

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    DC: Port.DC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({
        configuration: 'unipolar',
    }),
    capacitance: Port.capacitance.validator,
    reverseDiode: z.boolean().default(false),
    equivalentSeriesResistance: Port.equivalentSeriesResistance.validator,
    isolated: Port.isolated.validator,
    parallelableCapacity: ParallelableCapacity.validator,
    purpose: PortPurpose.validator,
    resistance: Resistance.validator,
}).default({
    powerFlowDirection: 'bidirectional',
});

const ChargerBase = z.object({
    type: z.literal('charger').default('charger'),
    electrical: z
        .object({
            ports: z.array(port).default([undefined]),
            standards: z.array(z.string()).default([]),
            chargingPorts: z
                .array(
                    z.object({
                        id: z.string().default(() => uid(10)),

                        connector: ChargerConnector.validator.nullable().default(null),
                        voltageType: z.enum(['AC', 'DC']).nullable().default(null),
                        // @giel Check these voltage / current & power, can we replace them by the Voltage/Current/Power validators?
                        voltage: AbstractMeasurement.minMax.default({
                            unit: 'V',
                        }),
                        current: AbstractMeasurement.minMax.default({
                            unit: 'A',
                        }),
                        power: AbstractMeasurement.minNomMax.default({
                            unit: 'W',
                        }),
                        features: ChargerFeatures.validator.default([]),
                    }),
                )
                .default([
                    {
                        voltageType: 'AC',
                    },
                ]),
            isolationVoltage: IsolationVoltage.validator,
        })
        .default({}),
    environmental: environmentalWithCoolingMethod,
});

export const ChargerValidator = ComponentValidator.merge(ChargerBase);
export const ChargerPayloadValidator = ComponentPayloadValidator.merge(ChargerBase);

export const ChargerDefinition: ComponentDefinition = {
    type: 'charger',
    name: 'EV Charger',
    plural: 'EV Chargers',
    synonyms: ['EV', 'EV Charger'],
    indicator: 'CHG',
    actsAsConverter: true,
    hasDynamicNumberOfPorts: true,
    interoperabilityDataModel: true,
    ports: {
        min: 1,
        max: Infinity,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        allowedControlMethods: ['constant-power', 'power-voltage', 'constant-voltage'],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: ChargerValidator,
        payload: ChargerPayloadValidator,
    },
};

export type Charger = z.infer<typeof ChargerValidator>;
export type ChargerPayload = z.infer<typeof ChargerPayloadValidator>;
